"use client"

import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu"
import { Column, Table } from "@tanstack/react-table"
import { Settings2 } from "lucide-react"

import { Button } from "@/Components/ui/button"
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/Components/ui/dropdown-menu"
import React from "react"
import { AppContext } from "../../App"
import { WizardContext } from "../wizard/wizardProvider"

function handleCheckChanged<TData>(tableName: string, table: Table<TData>, value: boolean, column: Column<TData, unknown>) {
    
    column.toggleVisibility(!!value);

    const columnVisibilty: { columnId: string, isVisible: boolean }[] = table
        .getAllColumns()
        .filter(
            (col) =>
                typeof col.accessorFn !== "undefined" && col.getCanHide()
        )
        .map((col) => ({
            columnId: col.id,
            isVisible: col.id == column.id ? value : col.getIsVisible()
        }));
    
    window.localStorage.setItem(tableName + "_columnVisibility", JSON.stringify(columnVisibilty));
}

interface DataTableViewOptionsProps<TData> {
    table: Table<TData>
    setAlternateRowColour?: React.Dispatch<React.SetStateAction<boolean>>
    alternateRowColour?: boolean
    columnDisplayNames?: [string, string][]
    tableName: string 
}

export function DataTableViewOptions<TData>({
    table,
    setAlternateRowColour,
    alternateRowColour,
    columnDisplayNames,
    tableName,
}: DataTableViewOptionsProps<TData>) {
    const app = React.useContext(AppContext);
    const wizard = React.useContext(WizardContext);

    const [openMenu, setOpenMenu] = React.useState(false);

    React.useEffect(() => {
        if (wizard.showWizardPointer && wizard.currentStep?.sendClick && wizard.currentStep.stepComponentId === "viewOptionsButton") {
            let timeoutId = setTimeout(() => setOpenMenu(true), 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [wizard.showWizardPointer, wizard.currentStep]);

    return (
        <DropdownMenu open={openMenu} onOpenChange={setOpenMenu} modal={false}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    id="viewOptionsButton"
                    className="ml-2 hidden h-8 lg:flex mt-1"
                >
                    <Settings2 />
                    View
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[150px]">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table
                    .getAllColumns()
                    .filter(
                        (column) =>
                            typeof column.accessorFn !== "undefined" && column.getCanHide()
                    )
                    .map((column) => {
                        return (
                            <DropdownMenuCheckboxItem
                                key={column.id}
                                className="capitalize"
                                checked={column.getIsVisible()}
                                onCheckedChange={(value) => handleCheckChanged(app.applicationUser?.id + "_" + tableName, table, value, column)}
                                // Prevent menu from closing on click
                                onSelect={(e) => e.preventDefault()}
                            >
                                {columnDisplayNames
                                    ? columnDisplayNames.find((columnDisplayName) => column.id === columnDisplayName[0])
                                        ? columnDisplayNames.find((columnDisplayName) => column.id === columnDisplayName[0])![1]
                                        : column.id
                                    : column.id}
                            </DropdownMenuCheckboxItem>
                        )
                    })}
                    {setAlternateRowColour && (
                        <>
                            <DropdownMenuLabel>Style Options</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuCheckboxItem
                                checked={alternateRowColour}
                                onCheckedChange={(value) => setAlternateRowColour(value)}
                                onSelect={(e) => e.preventDefault()}
                            >
                                Alternate Row Colors
                            </DropdownMenuCheckboxItem>
                        </>
                    )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
